import { resources } from "@constants/hardcoded-strings";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { AddingLandingPageModal } from "@containers/modals/add-landing-page-modal/add-landing-page-modal";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LandingPageTypes } from "@enums/landing-page-types";
import { LayoutTypes } from "@enums/layout-types";
import { ModalTypes } from "@enums/modal-types";
import { PageLevelTypes } from "@enums/page-level-types";
import {
    STATIC_REDIRECT_URLS
} from "@iddataweb/idw-common-ui/constants";
import {
    AXNUIContent,
    AXNUILayout,
    SelectOption
} from "@iddataweb/idw-common-ui/types";
import {
    getRedirectUrlWithoutHostname
} from "@iddataweb/idw-common-ui/utils";
import { ThemingLandingPageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";

import commonStyles from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-component.less";
import { CustomLandingPageContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-landing-page-tab-container/custom-landing-page-container/custom-landing-page-container";
import { CustomerRedirectUrlContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-landing-page-tab-container/customer-redirect-url-container/customer-redirect-url-container";
import { StaticLandingPageContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-landing-page-tab-container/static-landing-page-container/static-landing-page-container";
import { RedirectUrlInformation, BaseRedirectUrl } from "@idw-types/redirect-url-information";
import { CustomLandingPagesContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-landing-page-tab-container/custom-landing-pages-container/custom-landing-pages-container";
import { toObject } from "@utils/object-util";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";

type LandingPageSectionsContainerProps = {
    isDisabled: boolean;
    isTheming: boolean;
    showOriginBadge: boolean;
    validationErrors: ThemingLandingPageValidationErrors;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUILayout: AXNUILayout;
    pageLevel: PageLevelTypes;
    redirectUrlsInformation: RedirectUrlInformation;
    handleRemoveRedirect: (index: number, redirectUrlOrigin: RedirectUrlOriginTypes) => void;
    handleAddRedirect: (newRedirectUrl: string) => void;
    handleRedirectChangeAtIndex: (newRedirectUrl: string, index: number, redirectUrl: string, redirectUrlOrigin: RedirectUrlOriginTypes) => void;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUILayoutPropertyValue: (axnuiLayout: AXNUILayout) => void;
}

export const LandingPageSectionsContainer = (props: LandingPageSectionsContainerProps) => {
    const {
        isDisabled,
        isTheming,
        showOriginBadge,
        validationErrors,
        axnuiContent,
        axnuiLayout,
        inheritedAXNUIContent,
        inheritedAXNUILayout,
        pageLevel,
        redirectUrlsInformation,
        handleAddRedirect,
        handleRemoveRedirect,
        handleRedirectChangeAtIndex,
        updateAXNUIContentPropertyValue,
        updateAXNUILayoutPropertyValue
    } = props;

    const modalContext = useModalContext();
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        customRedirectUrlsWithOriginalIndex,
        staticRedirectUrlsWithOriginalIndex,
        customerRedirectUrlsWithOriginalIndex,
    } = redirectUrlsInformation;

    const [selectedCustomLandingPage, setSelectedCustomLandingPage] = useState(customRedirectUrlsWithOriginalIndex.length > 0 ? customRedirectUrlsWithOriginalIndex[0] : null);
    const resource = resources.pages.verificationServiceDetails.tabs.theming.subTabs.landingPage;


    useEffect(() => {
        if (customRedirectUrlsWithOriginalIndex.length > 0) {
            //if we have more than 1 redirect url and there is a selected custom redirect url
            if (selectedCustomLandingPage) {
                //check if the selected url still exists
                const previouslySelectedCustomLandingPage = customRedirectUrlsWithOriginalIndex.find(lp => lp.index === selectedCustomLandingPage.index);

                //if the previously selected landing page no longer exists, set current selected to first in list
                if (!previouslySelectedCustomLandingPage) {
                    setSelectedCustomLandingPage(customRedirectUrlsWithOriginalIndex[0]);
                }
                //if the url at the index is different, we need to find the correct index
                else if (previouslySelectedCustomLandingPage.url !== selectedCustomLandingPage.url) {
                    const positionWithSameUrl = customRedirectUrlsWithOriginalIndex.find(lp => lp.url === selectedCustomLandingPage.url);
                    //if there is a matching url at some index, set the selected url to object at that index
                    if (positionWithSameUrl) {
                        setSelectedCustomLandingPage(positionWithSameUrl);
                    } else {
                        //there is no matching url - set selected to first object in list
                        setSelectedCustomLandingPage(customRedirectUrlsWithOriginalIndex[0]);
                    }
                } else {
                    setSelectedCustomLandingPage(previouslySelectedCustomLandingPage);
                }
            } else {
                //there is no selected landing page - set it to first object in list 
                setSelectedCustomLandingPage(customRedirectUrlsWithOriginalIndex[0]);
            }
        } else {
            //there are no urls, set selected to null
            setSelectedCustomLandingPage(null);
        }
    }, [customRedirectUrlsWithOriginalIndex]);

    const onClickAddCustomLandingPage = () => {
        onClickAddLandingPage(LandingPageTypes.CUSTOM);
    };

    const onClickAddStaticLandingPage = () => {
        onClickAddLandingPage(LandingPageTypes.STATIC);
    };

    const onClickAddCustomerRedirectUrl = () => {
        onClickAddLandingPage(LandingPageTypes.CUSTOMER);
    };

    const onClickContinue = (landingPageName: string) => {
        handleAddRedirect(landingPageName);
    };

    const customRedirectUrlsMap = useMemo(()=> {
        return toObject<BaseRedirectUrl>(customRedirectUrlsWithOriginalIndex, 'url');
    }, [customRedirectUrlsWithOriginalIndex]);

    const customerRedirectUrlsMap = useMemo(()=> {
        return toObject<BaseRedirectUrl>(customerRedirectUrlsWithOriginalIndex, 'url');
    }, [customerRedirectUrlsWithOriginalIndex]);

    const onClickAddLandingPage = (landingPageType: LandingPageTypes) => {
        const selectedStaticLandingPageUrls = staticRedirectUrlsWithOriginalIndex.map(urlWithOrigin => urlWithOrigin.url);

        const renderAddLandingPageModal = (onModalClose: () => void) => {
            return (
                <AddingLandingPageModal
                    selectedStaticLandingPageUrls={selectedStaticLandingPageUrls}
                    landingPageType={landingPageType}
                    customRedirectUrlsMap={customRedirectUrlsMap}
                    customerRedirectUrlsMap={customerRedirectUrlsMap}
                    onClickContinue={onClickContinue}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddLandingPage,
            renderFunction: renderAddLandingPageModal
        });
    };

    const onChangeSelectedCustomLandingPage = (newValue: SelectOption) => {
        const newCustomLP = customRedirectUrlsWithOriginalIndex.find(lp => lp.url === newValue.value);
        setSelectedCustomLandingPage(newCustomLP);
    };

    const handleCustomRedirectChangeAtIndex = (newRedirectUrl: string, index: number, redirectUrl: string, redirectUrlOrigin: RedirectUrlOriginTypes) => {
        setSelectedCustomLandingPage({ url: newRedirectUrl, index, redirectUrlOrigin });
        handleRedirectChangeAtIndex(newRedirectUrl, index, redirectUrl, redirectUrlOrigin);
    };

    const customLandingPageOptions: Array<SelectOption> = useMemo(() => {
        return customRedirectUrlsWithOriginalIndex.map(lp => ({
            label: getRedirectUrlWithoutHostname(lp.url),
            value: lp.url
        }));
    }, [customRedirectUrlsWithOriginalIndex]);

    const selectedCustomLandingPageOption: SelectOption = useMemo(() => {
        if (selectedCustomLandingPage) {
            return {
                label: getRedirectUrlWithoutHostname(selectedCustomLandingPage.url),
                value: selectedCustomLandingPage.url
            };
        }

        return null;
    }, [selectedCustomLandingPage]);

    return (
        <div>
            <ThemingFormSection title={resource.sections.customLandingPage.title} showSeparator={true}>
                <span className={commonStyles.description}>
                    {resource.sections.customLandingPage.description}
                </span>
                {customRedirectUrlsWithOriginalIndex.length > 0 ? (
                    <>
                        {isTheming ? (
                            <>
                                <SelectFormGroup
                                    automationId={`custom-landing-page-select`}
                                    controlId={`custom-landing-page-select`}
                                    ariaLabel={resource.inputs.landingPage.label}
                                    disabled={false}
                                    isClearable={false}
                                    isRequired={true}
                                    isSearchable={false}
                                    onChange={onChangeSelectedCustomLandingPage}
                                    options={customLandingPageOptions}
                                    selectedOption={selectedCustomLandingPageOption}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                />
                                {selectedCustomLandingPage && (
                                    <CustomLandingPageContainer
                                        isTheming={isTheming}
                                        showOriginBadge={showOriginBadge}
                                        key={`theming-landing-page-${selectedCustomLandingPage.url}-container`}
                                        index={selectedCustomLandingPage.index}
                                        redirectUrl={selectedCustomLandingPage.url}
                                        redirectUrlOrigin={selectedCustomLandingPage.redirectUrlOrigin}
                                        isDisabled={isDisabled || !selectedCustomLandingPage.isEditable}
                                        axnuiContent={axnuiContent}
                                        axnuiLayout={axnuiLayout}
                                        layout={layout}
                                        labelWidth={labelWidth}
                                        inheritedAXNUIContent={inheritedAXNUIContent}
                                        inheritedAXNUILayout={inheritedAXNUILayout}
                                        validationErrors={validationErrors}
                                        pageLevel={pageLevel}
                                        handleRemoveRedirect={handleRemoveRedirect}
                                        handleRedirectChangeAtIndex={handleCustomRedirectChangeAtIndex}
                                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                                    />
                                )}
                            </>
                        ) : (
                            <CustomLandingPagesContainer
                                isDisabled={isDisabled}
                                showOriginBadge={showOriginBadge}
                                pageLevel={pageLevel}
                                labelWidth={labelWidth}
                                layout={layout}
                                customRedirectUrlsWithOriginalIndex={customRedirectUrlsWithOriginalIndex}
                                handleRemoveRedirect={handleRemoveRedirect}
                                handleCustomRedirectChangeAtIndex={handleCustomRedirectChangeAtIndex}
                            />
                        )}

                    </>
                ) : (
                    <Alert>{resource.sections.customLandingPage.noneAdded}</Alert>
                )}
                {!isDisabled && (
                    <Button className={commonStyles.description} onClick={onClickAddCustomLandingPage}>
                        {resource.buttons.addLandingPage.label(LandingPageTypes.CUSTOM)}
                    </Button>
                )}
            </ThemingFormSection>
            <ThemingFormSection title={resource.sections.staticLandingPages.title} showSeparator={true}>
                <span className={commonStyles.description}>
                    {resource.sections.staticLandingPages.description}
                </span>
                {staticRedirectUrlsWithOriginalIndex.length > 0 ? (
                    <>
                        {staticRedirectUrlsWithOriginalIndex.map((redirectUrlWithIndex, index) => (
                            <StaticLandingPageContainer
                                labelWidth={labelWidth}
                                layout={layout}
                                showOriginBadge={showOriginBadge}
                                isDisabled={isDisabled || !redirectUrlWithIndex.isEditable}
                                index={redirectUrlWithIndex.index}
                                redirectUrl={redirectUrlWithIndex.url}
                                redirectUrlOrigin={redirectUrlWithIndex.redirectUrlOrigin}
                                handleRemoveRedirect={handleRemoveRedirect}
                                key={`static-landing-page-${redirectUrlWithIndex.url}-${index}-container`}
                            />
                        ))}
                    </>
                ) : (
                    <Alert>{resource.sections.staticLandingPages.noneAdded}</Alert>
                )}
                {staticRedirectUrlsWithOriginalIndex.length < STATIC_REDIRECT_URLS.length && !isDisabled && (
                    <Button className={commonStyles.description} onClick={onClickAddStaticLandingPage}>
                        {resource.buttons.addLandingPage.label(LandingPageTypes.STATIC)}
                    </Button>
                )}
            </ThemingFormSection>
            <ThemingFormSection title={resource.sections.customerRedirectUrls.title} showSeparator={false}>
                <span className={commonStyles.description}>
                    {resource.sections.customerRedirectUrls.description}
                </span>
                {customerRedirectUrlsWithOriginalIndex.length > 0 ? (
                    <>
                        {customerRedirectUrlsWithOriginalIndex.map((redirectUrlWithIndex, index) => (
                            <CustomerRedirectUrlContainer
                                labelWidth={labelWidth}
                                showOriginBadge={showOriginBadge}
                                layout={LayoutTypes.ROW}
                                isDisabled={isDisabled || !redirectUrlWithIndex.isEditable}
                                index={redirectUrlWithIndex.index}
                                redirectUrl={redirectUrlWithIndex.url}
                                redirectUrlOrigin={redirectUrlWithIndex.redirectUrlOrigin}
                                handleRemoveRedirect={handleRemoveRedirect}
                                handleRedirectChangeAtIndex={handleRedirectChangeAtIndex}
                                key={`customer-redirect-url-${redirectUrlWithIndex.url}-${index}-container`}
                            />
                        ))}
                    </>
                ) : (
                    <Alert>{resource.sections.customerRedirectUrls.noneAdded}</Alert>
                )}
                {!isDisabled && (
                    <Button className={commonStyles.description} onClick={onClickAddCustomerRedirectUrl}>
                        {resource.buttons.addLandingPage.label(LandingPageTypes.CUSTOMER)}
                    </Button>
                )}
            </ThemingFormSection>
        </div>
    )
}